import './UploadPublicAssetModal.scss';
import React, { useState, useContext } from 'react';
import { I18nReactContext } from "../../../../contexts/I18nContext";
import { Modal } from "../../../../Components/Smart/Modal/Modal";
import { buttonColors } from "../../../../Components/Pickers/InputTheme";
import { FormInput } from "../../../../Components/Form/Input/FormInput";
import { uploadPublicAssetAPI } from "../../../../Services/PublicAssets_api";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LoadingSpinner from "../../../../Components/LoadingSpinner/LoadingSpinner";
import {Button} from "../../../Form/Button/Button";
import { Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

export const UploadPublicAssetModal = ({ isOpen, onClose, onSuccess }) => {
    const { i18n } = useContext(I18nReactContext);
    const [files, setFiles] = useState([]);
    const [presentationName, setPresentationName] = useState('');
    const [description, setDescription] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);

    // Reset state when modal is opened
    React.useEffect(() => {
        if (isOpen) {
            setFiles([]);
            setPresentationName('');
            setDescription('');
            setIsUploading(false);
            setUploadProgress(0);
            setError(null);
            setPreview(null);
        }
    }, [isOpen]);

    // Generate preview for the first file when files are selected
    React.useEffect(() => {
        if (files.length === 0) {
            setPreview(null);
            return;
        }

        // Use the first file for preview
        const firstFile = files[0];
        const objectUrl = URL.createObjectURL(firstFile);
        setPreview(objectUrl);

        // Free memory when component unmounts
        return () => URL.revokeObjectURL(objectUrl);
    }, [files]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFiles = Array.from(e.target.files);

            // Check if adding these files would exceed the limit of 50
            if (files.length + selectedFiles.length > 50) {
                setError(i18n.translate('superAdmin.assets.upload.tooManyFiles', { context: { max: 50 } }) || 
                         'Maximum of 50 files allowed');
                return;
            }

            // Add new files to existing files
            setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
            setError(null);

            // Use first file name as presentation name if not set and this is the first file
            if (!presentationName && files.length === 0) {
                // Remove extension from file name
                const fileName = selectedFiles[0].name.split('.').slice(0, -1).join('.');
                setPresentationName(fileName);
            }
        }
    };

    const removeFile = (indexToRemove) => {
        setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            setError(i18n.translate('superAdmin.assets.upload.noFileSelected'));
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const onProgress = (progress) => {
                setUploadProgress(Math.round((progress.loaded / progress.total) * 100));
            };

            const result = await uploadPublicAssetAPI({
                files: files,
                presentationName,
                description,
                onProgress
            });

            if (onSuccess) {
                onSuccess(result);
            }

            onClose();
        } catch (err) {
            console.error('Error uploading files:', err);
            setError(i18n.translate('superAdmin.assets.upload.error'));
        } finally {
            setIsUploading(false);
        }
    };

    const isImage = files.length > 0 && files[0].type.startsWith('image/');
    const isVideo = files.length > 0 && files[0].type.startsWith('video/');

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
        >
            <div className="modalContent UploadPublicAssetModal">
                <h2 className="modalTitle">{i18n.translate('superAdmin.assets.upload.title')}</h2>
                {isUploading ? (
                    <div className="uploadingContainer">
                        <LoadingSpinner />
                        <div className="progressText">
                            {i18n.translate('superAdmin.assets.upload.uploading')} ({uploadProgress}%)
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="fileUploadSection">
                            <div 
                                className={`dropZone ${files.length > 0 ? 'hasFile' : ''}`}
                                onClick={() => document.getElementById('fileInput').click()}
                            >
                                {preview ? (
                                    isImage ? (
                                        <img src={preview} alt="Preview" className="filePreview" />
                                    ) : isVideo ? (
                                        <video src={preview} className="filePreview" controls />
                                    ) : (
                                        <div className="fileIcon">
                                            <CloudUploadIcon />
                                            <div>{files[0].name}</div>
                                        </div>
                                    )
                                ) : (
                                    <div className="uploadPrompt">
                                        <CloudUploadIcon className="uploadIcon" />
                                        <div>{i18n.translate('superAdmin.assets.upload.dragDrop')}</div>
                                    </div>
                                )}
                                <input
                                    id="fileInput"
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    multiple
                                />
                            </div>
                            <div className="fileTypeInfo">
                                {i18n.translate('superAdmin.assets.upload.supportedTypes')}
                            </div>

                            {files.length > 0 && (
                                <div className="selectedFilesContainer">
                                    <div className="selectedFilesHeader">
                                        {i18n.translate('superAdmin.assets.upload.selectedFiles', { context: { count: files.length } }) || 
                                         `Selected Files (${files.length})`}
                                    </div>
                                    <div className="selectedFilesList">
                                        {files.map((file, index) => (
                                            <Chip
                                                key={index}
                                                label={file.name}
                                                onDelete={() => removeFile(index)}
                                                deleteIcon={<DeleteIcon />}
                                                className="fileChip"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="formSection">
                            <FormInput
                                label={i18n.translate('superAdmin.assets.upload.presentationName')}
                                value={presentationName}
                                onChange={(e) => setPresentationName(e.target.value)}
                                placeholder={i18n.translate('superAdmin.assets.upload.presentationNamePlaceholder')}
                            />

                            <FormInput
                                label={i18n.translate('superAdmin.assets.upload.description')}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={i18n.translate('superAdmin.assets.upload.descriptionPlaceholder')}
                                multiline={true}
                                minRows={4}
                            />
                        </div>

                        {error && <div className="errorMessage">{error}</div>}

                        <div className="buttonContainer">
                            <Button
                                text={i18n.translate('superAdmin.assets.upload.cancel')}
                                onClick={onClose}
                                color={buttonColors.GREY}
                            />
                            <Button
                                text={files.length > 1 
                                    ? i18n.translate('superAdmin.assets.upload.uploadFiles', { context: { count: files.length } }) || `Upload Files (${files.length})` 
                                    : i18n.translate('superAdmin.assets.upload.upload')}
                                onClick={handleUpload}
                                color={buttonColors.GOLD}
                                disabled={files.length === 0}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
